import { Button, Link } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer';
import { SingleSelectForm } from 'components/single select form/single-select-form';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { startCase } from 'lodash-es';
import { parseMomentUtc } from 'utils/dates';
import { DcaScenariosResponse } from 'types/api-responses';
import { dcaScenariosListAtom } from './atoms';
import style from './dca-scenario-selection-modal.module.scss';

interface DcaScenarioSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (scenario: { id: number; name: string }) => void;
  currentDcaScenarioId?: string | null;
}

export const DcaScenarioSelectionModal = ({
  open,
  onClose,
  onSelect,
  currentDcaScenarioId,
}: DcaScenarioSelectionModalProps) => {
  const { loading, data: scenarios } = useAtomValue(dcaScenariosListAtom);

  const value = useMemo(() => {
    if (!currentDcaScenarioId || scenarios.length === 0) return null;
    return scenarios.find((s) => String(s.id) === currentDcaScenarioId) || null;
  }, [currentDcaScenarioId, scenarios]);

  return (
    <Drawer
      lockBackgroundScroll
      open={open}
      direction="right"
      size="100%"
      className={style.drawer}
      onClose={onClose}
    >
      {open && (
        <>
          <DrawerHeader className={style.header}>
            <div className={style.drawerHeader}>
              <div>
                <h2 className={style.title}>DCA Scenarios</h2>
                <p className={style.description}>
                  Select a DCA scenario to link to this Type Curve Scenario.
                </p>
              </div>
              <Button
                hasIconOnly
                size="lg"
                kind="ghost"
                onClick={onClose}
                iconDescription="Close"
                tooltipAlignment="center"
                tooltipPosition="bottom"
                renderIcon={(props) => <Close size={20} {...props} />}
              />
            </div>
          </DrawerHeader>
          <DrawerContent>
            <div className={style.scrollArea}>
              <SingleSelectForm
                id="tc-dca-scenario-selector"
                options={scenarios}
                value={value}
                isLoading={loading}
                onChange={(val) => {
                  if (val) {
                    onSelect({ id: val.id, name: val.name });
                  }
                }}
                onSearch={(text, option) =>
                  option.name.toLowerCase().includes(text.toLowerCase())
                }
                renderLabel={(scenario) => (
                  <DcaScenarioLabel scenario={scenario} />
                )}
                formClassName={style.dcaScenarioForm}
              />
            </div>
          </DrawerContent>
        </>
      )}
    </Drawer>
  );
};

const DcaScenarioLabel = ({ scenario }: { scenario: DcaScenariosResponse }) => {
  const { createdDate, updatedDate } = useMemo(() => {
    return {
      createdDate: scenario.createdAt
        ? parseMomentUtc(scenario.createdAt).format('YYYY-MM-DD')
        : '--',
      updatedDate: scenario.updatedAt
        ? parseMomentUtc(scenario.updatedAt).format('YYYY-MM-DD')
        : '--',
    };
  }, [scenario.createdAt, scenario.updatedAt]);

  const scenarioName = useMemo(() => {
    const name = scenario.name;
    switch (name) {
      case 'pdp default':
        return 'PDP Default';
      case 'MLLE':
        return 'MLLE';
      default:
        return startCase(name);
    }
  }, [scenario.name]);

  return (
    <div className={style.scenarioLabel}>
      <Link
        className={style.scenarioName}
        target="_blank"
        href={`/dca_scenarios/${scenario.id}`}
      >
        {scenarioName}
      </Link>
      <div className={style.scenarioCreator}>
        {scenario.createdBy || 'Unknown'}
      </div>
      <div className={style.scenarioDetails}>
        {`Created: ${createdDate} | Updated: ${updatedDate}`}
      </div>
    </div>
  );
};
